import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import Modal from "../../components/ui-components/Modal";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import {
  getProviderMappings,
  getClassificationCoverage,
  createMappingOverride,
  deleteMappingOverride,
  updateMappingOverride,
} from "../allocation/providerMappingApi";
import { getSourceTypes } from "../allocation/taxonomyApi";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

// ─── Override modal ────────────────────────────────────────────────────────────
const OverrideModal = ({ show, onClose, provider, sourceTypes, queryClient, initial }) => {
  const [action, setAction] = useState(initial?.action ?? "map");
  const [matchType, setMatchType] = useState(initial?.match_type ?? "service_name");
  const [matchPattern, setMatchPattern] = useState(initial?.match_pattern ?? "");
  const [sourceTypeId, setSourceTypeId] = useState(initial?.source_type_id ?? "");
  const [reason, setReason] = useState(initial?.reason ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = !!initial;
  const showTypePicker = action === "map" || action === "route_shared";
  const filteredTypes =
    action === "route_shared"
      ? sourceTypes.filter((t) => t.default_shared_flag === true)
      : sourceTypes;

  const reset = () => {
    setAction("map");
    setMatchType("service_name");
    setMatchPattern("");
    setSourceTypeId("");
    setReason("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!matchPattern.trim()) return;
    if (showTypePicker && !sourceTypeId) {
      setError("Select a source type");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        provider,
        match_type: matchType,
        match_pattern: matchPattern.trim(),
        action,
        source_type_id: showTypePicker ? sourceTypeId || undefined : undefined,
        reason,
      };
      if (isEdit) {
        await updateMappingOverride(initial.override_id, payload);
      } else {
        await createMappingOverride(payload);
      }
      handleClose();
      queryClient.invalidateQueries(["provider-mappings"]);
      queryClient.invalidateQueries(["classification-coverage"]);
    } catch (e) {
      setError(
        e?.status === 409
          ? "An override for this pattern already exists."
          : isEdit
          ? "Failed to update override"
          : "Failed to create override"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      close={handleClose}
      title={isEdit ? "Edit override" : "Add override"}
      description="Override the system mapping for a specific pattern."
      confirmButton={saving ? "Saving…" : isEdit ? "Save" : "Create"}
      confirmAction={submit}
      cancelButton="Cancel"
    >
      <div className="mt-2 space-y-3">
        <div>
          <label htmlFor="om-action" className="block text-xs font-medium text-text-2 mb-1">
            Action
          </label>
          <select
            id="om-action"
            aria-label="action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm"
          >
            <option value="map">Map</option>
            <option value="ignore">Ignore</option>
            <option value="route_shared">Route shared</option>
            <option value="request">Request</option>
          </select>
        </div>

        <div>
          <label htmlFor="om-match-type" className="block text-xs font-medium text-text-2 mb-1">
            Match type
          </label>
          <select
            id="om-match-type"
            aria-label="match type"
            value={matchType}
            onChange={(e) => setMatchType(e.target.value)}
            className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm"
          >
            <option value="service_name">Service name</option>
            <option value="sku_regex">SKU regex</option>
            <option value="usage_type">Usage type</option>
            <option value="api_endpoint">API endpoint (forward-compat)</option>
          </select>
        </div>

        <div>
          <label htmlFor="om-pattern" className="block text-xs font-medium text-text-2 mb-1">
            Pattern
          </label>
          <input
            id="om-pattern"
            aria-label="pattern"
            value={matchPattern}
            onChange={(e) => setMatchPattern(e.target.value)}
            className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm"
            placeholder="e.g. Cloud SQL"
          />
        </div>

        {showTypePicker && (
          <div>
            <label htmlFor="om-type" className="block text-xs font-medium text-text-2 mb-1">
              Source type
            </label>
            <select
              id="om-type"
              aria-label="source type"
              value={sourceTypeId}
              onChange={(e) => setSourceTypeId(e.target.value)}
              className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm"
            >
              <option value="">— select —</option>
              {filteredTypes.map((t) => (
                <option key={t.source_type_id} value={t.source_type_id}>
                  {t.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="om-reason" className="block text-xs font-medium text-text-2 mb-1">
            Reason
          </label>
          <input
            id="om-reason"
            aria-label="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm"
            placeholder="Optional"
          />
        </div>

        {error && <p className="text-xs text-danger mt-1">{error}</p>}
      </div>
    </Modal>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const ProviderMappingPage = () => {
  const queryClient = useQueryClient();
  const mappingsQ = useQuery(["provider-mappings"], getProviderMappings);
  const coverageQ = useQuery(["classification-coverage"], getClassificationCoverage);
  const typesQ = useQuery(["taxonomy-source-types"], getSourceTypes);

  const [provider, setProvider] = useState("gcp");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode; override row = edit mode
  const [deleteError, setDeleteError] = useState(null);

  const system = (mappingsQ.data?.system || []).filter((m) => m.provider === provider);
  const overrides = (mappingsQ.data?.overrides || []).filter((o) => o.provider === provider);
  const activeOverrides = overrides.filter((o) => o.action !== "request");
  const requestOverrides = overrides.filter((o) => o.action === "request");
  const coverage = coverageQ.data || { byType: [], unclassifiedCost: 0, ignoredCost: 0, totalCost: 0 };
  const sourceTypes = useMemo(() => typesQ.data?.source_types || [], [typesQ.data]);
  const typeName = (id) => sourceTypes.find((t) => t.source_type_id === id)?.display_name || id || "—";

  // System-mappings columns, rendered through the shared FlexTable (matching the /budgets table).
  const systemColumns = useMemo(
    () => [
      {
        accessor: "match_type",
        Header: () => <span>Match type</span>,
        Cell: ({ value }) => <span className="text-text-2">{value}</span>,
        flex: 1,
        minWidth: 140,
      },
      {
        accessor: "match_pattern",
        Header: () => <span>Pattern</span>,
        Cell: ({ value }) => <span className="font-medium text-text">{value}</span>,
        flex: 1,
        minWidth: 160,
      },
      {
        accessor: "source_type_id",
        Header: () => <span>→ Type</span>,
        Cell: ({ value }) => (
          <span className="text-text-2">
            {sourceTypes.find((t) => t.source_type_id === value)?.display_name || value || "—"}
          </span>
        ),
        flex: 1,
        minWidth: 140,
      },
      {
        accessor: "confidence_default",
        Header: () => <span>Confidence</span>,
        Cell: ({ value }) => <span className="text-text-2">{value}</span>,
        width: 120,
        minWidth: 100,
      },
      {
        accessor: "priority",
        align: "right",
        Header: () => <span>Priority</span>,
        Cell: ({ value }) => <span className="text-text-2">{value}</span>,
        width: 110,
        minWidth: 90,
      },
    ],
    [sourceTypes]
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    setDeleteError(null);
    try {
      await deleteMappingOverride(id);
      queryClient.invalidateQueries(["provider-mappings"]);
      queryClient.invalidateQueries(["classification-coverage"]);
    } catch (e) {
      setDeleteError("Failed to delete override. Please try again.");
    }
  };

  const overrideRow = (o) => (
    <div
      key={o.override_id}
      className="grid grid-cols-6 gap-2 px-4 py-2 text-sm border-b border-border last:border-0 items-center"
    >
      <span className="text-text-2 flex items-center gap-1">
        {o.action}
        {o.action === "request" && (
          <span className="ml-1 inline-block px-1.5 py-0.5 text-xs rounded bg-warning-soft text-warning font-medium">
            Request
          </span>
        )}
      </span>
      <span className="text-text">{o.match_pattern}</span>
      <span className="text-text-2">{typeName(o.source_type_id)}</span>
      <span className="text-text-2">{o.reason || "—"}</span>
      <button
        type="button"
        onClick={() => openEdit(o)}
        className="text-xs text-primary hover:text-primary-hover text-left"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={() => handleDelete(o.override_id)}
        className="text-xs text-danger hover:text-danger text-left"
      >
        Remove
      </button>
    </div>
  );

  return (
    <>
      <AdministrationHeader title="Provider Mapping" showPeriodSelector={false} />

      <div className="ml-10 mt-6 mr-6 space-y-6">
        {/* Coverage panel */}
        <div className="bg-card rounded-card border border-card-bd p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-3">
            Classification Coverage
          </p>
          <div className="flex flex-wrap gap-4">
            {coverage.byType.map((row) => (
              <div key={row.value} className="text-sm">
                <span className="text-text-2">{row.value}</span>
                <span className="ml-2 font-medium text-text">{euro(row.monthlyCost)}</span>
              </div>
            ))}
            <div className="text-sm">
              <span className="text-text-2">Unclassified</span>
              <span className="ml-2 font-medium text-text">{euro(coverage.unclassifiedCost)}</span>
            </div>
            <div className="text-sm">
              <span className="text-text-2">Ignored</span>
              <span className="ml-2 font-medium text-text">{euro(coverage.ignoredCost)}</span>
            </div>
          </div>
        </div>

        {/* Provider tabs + content */}
        <div>
          {/* Provider switch — same segmented style as the /forecast-lab insight/allocation toggle */}
          <div className="mb-4 inline-flex rounded-seg border border-card-bd p-0.5">
            {["gcp", "openai"].map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={provider === p}
                onClick={() => setProvider(p)}
                className={`rounded-seg px-3 py-1 text-sm ${
                  provider === p
                    ? "bg-primary font-semibold text-white"
                    : "text-text-2 hover:text-text"
                }`}
              >
                {p === "gcp" ? "GCP" : "OpenAI"}
              </button>
            ))}
          </div>

          {/* System mappings */}
          <div className="mb-6">
            <TableCard
              title="System Mappings"
              subtitle={`Showing ${system.length} ${system.length === 1 ? "mapping" : "mappings"}`}
              isLoading={mappingsQ.isLoading}
              error={mappingsQ.error}
              errorMessage="Error loading mappings"
              onRetry={() => queryClient.invalidateQueries(["provider-mappings"])}
            >
              {system.length === 0 ? (
                <EmptyState
                  icon={icon({ name: "diagram-project", style: "light" })}
                  title="No system mappings"
                  message="No system mappings for this provider."
                />
              ) : (
                <FlexTable
                  columns={systemColumns}
                  data={system}
                  getRowId={(m) => m.mapping_id}
                />
              )}
            </TableCard>
          </div>

          {/* Tenant overrides */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium uppercase tracking-wide text-text-2">
                Overrides
              </p>
              <button
                type="button"
                onClick={openCreate}
                className="px-3 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover"
              >
                + Override
              </button>
            </div>

            {deleteError && (
              <div className="mb-2 px-4 py-2 text-sm text-danger bg-danger-soft border border-danger/30 rounded-lg">
                {deleteError}
              </div>
            )}

            <div className="bg-card rounded-card border border-card-bd overflow-hidden">
              <div className="grid grid-cols-6 gap-2 px-4 py-2 text-xs uppercase tracking-wide text-text-2 border-b border-border">
                <span>Action</span>
                <span>Pattern</span>
                <span>→ Type</span>
                <span>Reason</span>
                <span></span>
                <span></span>
              </div>

              {/* Active overrides */}
              {activeOverrides.map(overrideRow)}

              {/* Requests subsection */}
              {requestOverrides.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-text-2 bg-surface-2 border-t border-border">
                    Requests
                  </div>
                  {requestOverrides.map(overrideRow)}
                </>
              )}

              {overrides.length === 0 && (
                <div className="px-4 py-6 text-sm text-text-2">
                  No overrides for this provider.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <OverrideModal
        key={editing?.override_id ?? "new"}
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        provider={provider}
        sourceTypes={sourceTypes}
        queryClient={queryClient}
        initial={editing}
      />
    </>
  );
};

export default ProviderMappingPage;
